import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import type { FormEvent } from 'react';

import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { EmojiButton } from '../../components/ui/EmojiPicker';
import { Field } from '../../components/ui/Field';
import { Select, Textarea } from '../../components/ui/form';
import { ApiError } from '../../lib/api';
import { useEmojiComposer } from '../../lib/emoji';
import { useSession } from '../../stores/session';
import { useArtistTracks } from '../tracks/useArtistTracks';
import { Modal } from './Modal';
import { albums, type Album } from './api';

/**
 * Create or edit an album.
 *
 * One component for both because the fields are the same and the difference is
 * a single call. Two would drift - the create form gaining a field the edit
 * form never learned about is exactly how that happens.
 *
 * ## The tracks go on here too, when creating
 *
 * Making an album used to be three steps: fill this in, open the album,
 * then hunt for the picker. Nobody makes an album they do not intend to
 * put anything on, so what goes on it is asked at the moment somebody is
 * thinking about it. Editing keeps its own picker on the album's page,
 * where the order and what is already on it are visible.
 *
 * The album is made first and the tracks are added to it, one call each,
 * the way the album page's own picker does. So a track that will not go
 * on - withdrawn between the list loading and the press - leaves an album
 * that exists with the rest of them on it, which is a thing somebody can
 * see and fix, rather than a creation that half happened.
 */
export function AlbumEditor({
  album,
  onClose,
  onSaved,
}: {
  /** Absent when creating. */
  album?: Album;
  onClose: () => void;
  onSaved: (album: Album) => void;
}) {
  const [title, setTitle] = useState(album?.title ?? '');
  const [description, setDescription] = useState(album?.description ?? '');
  const [visibility, setVisibility] = useState<Album['visibility']>(album?.visibility ?? 'public');

  const emoji = useEmojiComposer(description, setDescription);

  const me = useSession((state) => state.user);
  const [chosen, setChosen] = useState<number[]>([]);
  const [partly, setPartly] = useState<string | null>(null);

  // Only what can go on an album: their own, and finished. The server says
  // the same thing; this is so nothing unpressable is offered.
  const { items, isPending: loadingTracks } = useArtistTracks(me?.username ?? '', {
    enabled: album === undefined && me !== null,
    all: true,
  });
  const candidates = items.filter((track) => track.status === 'ready');

  const save = useMutation({
    mutationFn: async () => {
      const draft = {
        title: title.trim(),
        description: description.trim() || null,
        visibility,
      };

      if (album) {
        return albums.update(album.id, draft);
      }

      const made = await albums.create(draft);
      const missed: string[] = [];

      // In the order they were picked, because that is the order they will
      // be in, and an album is an arrangement.
      for (const id of chosen) {
        try {
          await albums.addTrack(made.id, id);
        } catch {
          missed.push(candidates.find((track) => track.id === id)?.title ?? `track ${id}`);
        }
      }

      if (missed.length > 0) {
        setPartly(
          `The album was made, but ${missed.join(', ')} could not go on it. Add ${missed.length === 1 ? 'it' : 'them'} from the album.`,
        );
      }

      return made;
    },
    onSuccess: onSaved,
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();

    if (!title.trim()) return;

    save.mutate();
  };

  return (
    <Modal title={album ? 'Edit album' : 'New album'} onClose={onClose}>
      <form onSubmit={submit} className="flex flex-col gap-5" data-album-form>
        <Field
          label="Title"
          name="title"
          value={title}
          maxLength={120}
          required
          autoFocus
          disabled={save.isPending}
          onChange={(event) => setTitle(event.target.value)}
        />

        <div className="flex flex-col gap-1.5">
          <label htmlFor="album-description" className="text-sm font-medium text-soft">
            Description
          </label>
          <Textarea
            id="album-description"
            ref={emoji.ref as React.RefObject<HTMLTextAreaElement>}
            className="min-h-24"
            value={description}
            maxLength={2000}
            disabled={save.isPending}
            placeholder="What is this release? Optional."
            onChange={(event) => setDescription(event.target.value)}
          />
          <div className="-mt-0.5 flex">
            <EmojiButton onSelect={emoji.insert} direction="down" align="left" />
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="album-visibility" className="text-sm font-medium text-soft">
            Who can see it
          </label>
          <Select
            id="album-visibility"
            value={visibility}
            disabled={save.isPending}
            data-album-visibility
            options={[
                { value: 'public', label: 'Everyone' },
                { value: 'unlisted', label: 'Anyone with the link' },
                { value: 'private', label: 'Only me' },
              ]}
            onChange={(next) => setVisibility(next as Album['visibility'])}
          />
          <p className="text-xs text-faint">
            {/*
              Spelled out because the interaction surprises people: an album's
              visibility and its tracks' visibility are separate, which is what
              lets somebody stage a release publicly while its tracks are still
              private.
            */}
            Tracks keep their own visibility. A private track stays hidden even on a public album.
          </p>
        </div>

        {/*
          What goes on it, at the moment somebody is thinking about it.
          Creating only: the album's own page has a picker that can also
          see what is already on and in what order.
        */}
        {album === undefined && (
          <div className="flex flex-col gap-1.5">
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-sm font-medium text-soft">Tracks</span>
              <span className="meta text-ghost">
                {chosen.length === 0 ? 'optional' : `${chosen.length} chosen`}
              </span>
            </div>

            {loadingTracks ? (
              <p className="text-xs text-faint">Looking at what you have uploaded…</p>
            ) : candidates.length === 0 ? (
              <p className="text-xs text-faint">
                Nothing finished to put on it yet. You can make the album now and add tracks once
                they have uploaded.
              </p>
            ) : (
              <ul className="flex max-h-56 flex-col gap-0.5 overflow-y-auto" data-album-track-picker>
                {candidates.map((track) => {
                  const on = chosen.includes(track.id);

                  return (
                    <li key={track.id}>
                      <button
                        type="button"
                        disabled={save.isPending}
                        data-album-pick={track.id}
                        aria-pressed={on}
                        onClick={() =>
                          setChosen((held) =>
                            held.includes(track.id)
                              ? held.filter((id) => id !== track.id)
                              : [...held, track.id],
                          )
                        }
                        className={`flex w-full items-center gap-2.5 rounded-[var(--radius-control)] px-2 py-1.5 text-left transition-colors ${
                          on ? 'bg-raised text-ink' : 'text-soft hover:bg-raised/60'
                        }`}
                      >
                        <span
                          aria-hidden
                          className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border text-[10px] ${
                            on ? 'border-transparent bg-accent text-accent-ink' : 'border-line'
                          }`}
                        >
                          {on ? '✓' : ''}
                        </span>
                        <span className="min-w-0 flex-1 truncate text-[13px]">{track.title}</span>
                        {on && (
                          <span className="shrink-0 font-mono text-[10px] text-ghost">
                            {chosen.indexOf(track.id) + 1}
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}

            <p className="text-xs text-faint">
              They go on in the order you pick them, and can be reordered on the album afterwards.
            </p>
          </div>
        )}

        {partly !== null && <Alert tone="info">{partly}</Alert>}

        {save.isError && (
          <Alert tone="error">
            {save.error instanceof ApiError ? save.error.message : 'That could not be saved.'}
          </Alert>
        )}

        <div className="flex items-center gap-3">
          <Button type="submit" loading={save.isPending} disabled={!title.trim()}>
            {album ? 'Save changes' : 'Create album'}
          </Button>
          <Button variant="ghost" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
        </div>
      </form>
    </Modal>
  );
}
